import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Box, Stepper, Step, StepLabel, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import StepConnector, { stepConnectorClasses } from "@mui/material/StepConnector";
import CheckIcon from "@mui/icons-material/Check";

// Styles
import "./OpdStepper.css";

// Step Components
import Step1VisitDemographics from "./Steps/Step1VisitDemographics";
import Step2ChiefComplaints from "./Steps/Step2ChiefComplaints";
import Step3VitalsExamination from "./Steps/Step3VitalsExamination";
import Step4ScreeningRouting from "./Steps/Step4ScreeningRouting";
import Step5DiagnosticTests from "./Steps/Step5DiagnosticTests";
import Step6DiagnosisCounselling from "./Steps/Step6DiagnosisCounselling";
import Step7DrugsDispensation from "./Steps/Step7DrugsDispensation";

const BLUE = "#1E88E5";
const BORDER = "#D8DFEA";

const STEP_LABELS = [
  "Visit, Facility & Demographic Details",
  "Chief Complaints & Brief History",
  "Vitals & Clinical Examination",
  "Lab Tests",
  "Diagnosis & Counselling",
  "e-Consultation & Drugs Dispensation",
  "Screening, Routing, Referral & Follow-Up",
];

// Connector: jo hissa poora ho chuka hai woh solid blue, aage ka halka grey
const CustomStepConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 15,
    left: "calc(-50% + 18px)",
    right: "calc(50% + 18px)",
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: BORDER,
    borderTopWidth: 2,
    borderRadius: 1,
    transition: "border-color .3s ease",
  },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    borderColor: BLUE,
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    borderColor: BLUE,
  },
}));

/**
 * Step icon: poore ho chuke steps par tick, current par uska number
 * (ring ke saath), aur aane wale steps par halka number. Pehle har step
 * par tick dikhta tha — chahe woh abhi tak khula bhi na ho.
 */
const CustomStepIcon = (props) => {
  const { active, completed, icon } = props;

  const background = completed ? BLUE : active ? "#FFFFFF" : "#FFFFFF";
  const borderColor = completed || active ? BLUE : BORDER;
  const textColor = active ? BLUE : "#9AA5B8";

  return (
    <Box
      sx={{
        width: 30,
        height: 30,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: background,
        border: `2px solid ${borderColor}`,
        boxShadow: active ? `0 0 0 4px ${BLUE}1F` : "none",
        transition: "all .25s ease",
        zIndex: 1,
      }}
    >
      {completed ? (
        <CheckIcon sx={{ color: "#FFFFFF", fontSize: 16 }} />
      ) : (
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: textColor, lineHeight: 1 }}>
          {icon}
        </Typography>
      )}
    </Box>
  );
};

export default function OpdStepperWrapper() {
  const location = useLocation();

  const fromVisitHistory = Boolean(location.state?.fromVisitHistory);
  const incomingStep = location.state?.currentStep;
  const initialActiveStep = incomingStep ? Math.max(0, Math.min(6, Number(incomingStep) - 1)) : 0;

  const [activeStep, setActiveStep] = useState(initialActiveStep);
  // If coming from visit history, isFirstTimeAbha is false.
  // If coming from ABHA verification, isFirstTimeAbha is true until Step 1 is submitted.
  const [isFirstTimeAbha, setIsFirstTimeAbha] = useState(!fromVisitHistory);

  useEffect(() => {
    if (fromVisitHistory && location.state?.displayId) {
      localStorage.setItem("currentOpdDisplayId", location.state.displayId);
    }
  }, [fromVisitHistory, location.state?.displayId]);

  // Scroll to top whenever step changes forward or backward
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    const pageContainer = document.querySelector(".opd-stepper-page");
    if (pageContainer) {
      pageContainer.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [activeStep]);

  const handleNext = () => {
    setActiveStep((prev) => Math.min(STEP_LABELS.length - 1, prev + 1));
  };

  const handlePrev = () => {
    setActiveStep((prev) => Math.max(0, prev - 1));
  };

  const renderStepContent = (stepIndex) => {
    switch (stepIndex) {
      case 0:
        return (
          <Step1VisitDemographics
            nextStep={handleNext}
            isFirstTimeAbha={isFirstTimeAbha}
            onStep1Submitted={() => setIsFirstTimeAbha(false)}
          />
        );
      case 1:
        return <Step2ChiefComplaints nextStep={handleNext} prevStep={handlePrev} />;
      case 2:
        return <Step3VitalsExamination nextStep={handleNext} prevStep={handlePrev} />;
      case 3:
        return <Step5DiagnosticTests nextStep={handleNext} prevStep={handlePrev} />;
      case 4:
        return <Step6DiagnosisCounselling nextStep={handleNext} prevStep={handlePrev} />;
      case 5:
        return <Step7DrugsDispensation nextStep={handleNext} prevStep={handlePrev} />;
      case 6:
        return <Step4ScreeningRouting prevStep={handlePrev} />;
      default:
        return <Typography>Unknown Step</Typography>;
    }
  };

  return (
    <Box className="opd-stepper-page">
      {/* Brand Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "flex-end" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1.5,
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <img
            src="/images/e-opd-logo.png"
            alt="e-OPD Logo"
            style={{ height: 42, objectFit: "contain" }}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        </Box>

        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "#4A5670" }}>
          Step {activeStep + 1} of {STEP_LABELS.length} ·{" "}
          <Box component="span" sx={{ color: "#111C33", fontWeight: 700 }}>
            {STEP_LABELS[activeStep]}
          </Box>
        </Typography>
      </Box>

      {/* Stepper Header Card */}
      <Box className="opd-stepper-container">
        <Stepper activeStep={activeStep} alternativeLabel connector={<CustomStepConnector />}>
          {STEP_LABELS.map((label, index) => (
            <Step key={label}>
              <StepLabel StepIconComponent={CustomStepIcon}>
                <Typography
                  className={[
                    "opd-step-label",
                    activeStep === index ? "opd-step-label-active" : "",
                    index < activeStep ? "opd-step-label-done" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Dynamic Form Content */}
      <Box className="opd-form-card">
        {renderStepContent(activeStep)}
      </Box>
    </Box>
  );
}
